<template>
	<v-data-table
		ref="vDataTable"
		:value="value"
		:custom-filter="customFilter"
		:dense="dense"
		:disable-pagination="disablePagination"
		:fixed-header="fixedHeader"
		:footer-props="footerProps"
		:headers="headers"
		:hide-default-footer="hideDefaultFooter"
		:hide-default-header="hideDefaultHeader"
		:item-class="itemClass"
		:item-key="itemKey"
		:items="items"
		:items-per-page="itemsPerPage"
		:light="light"
		:must-sort="mustSort"
		:no-data-text="noDataText"
		:options="options"
		:page="page"
		:search="search"
		:server-items-length="serverItemsLength"
		:show-select="showSelect"
		:sort-by="sortBy"
		:sort-desc="sortDesc"
		v-bind="$attrs"
		v-on="other_listeners"
		@update:sort-by="$emit('update:sort-by', $event)"
		@update:sort-desc="$emit('update:sort-desc', $event)"
	>
		<!-- Explicit item slot -->
		<template v-if="$scopedSlots.item" v-slot:item="slotProps">
			<slot name="item" v-bind="slotProps" />
		</template>
		<!-- Explicit select-all slot -->
		<template v-if="$scopedSlots['data-table-select']" v-slot:data-table-select="slotProps">
			<slot name="data-table-select" v-bind="slotProps" />
		</template>
		<!-- Explicit header.* slots for column header customization -->
		<template v-for="(_, slotName) in header_scoped_slots" :slot="slotName" slot-scope="slotProps">
			<slot :name="slotName" v-bind="slotProps" />
		</template>
		<!-- Pass through all other scoped slots -->
		<template v-for="(_, slotName) in other_scoped_slots" :slot="slotName" slot-scope="slotProps">
			<slot :name="slotName" v-bind="slotProps" />
		</template>
	</v-data-table>
</template>

<script>
export default {
	model: {
		prop: 'value',
		event: 'input'
	},
	props: {
		value: {
			type: [Array, Object],
			default: () => []
		},
		customFilter: Function,
		dense: Boolean,
		disablePagination: Boolean,
		fixedHeader: Boolean,
		footerProps: Object,
		headers: Array,
		hideDefaultFooter: Boolean,
		hideDefaultHeader: Boolean,
		itemClass: [String, Function],
		itemKey: String,
		items: Array,
		itemsPerPage: [Number, String],
		light: Boolean,
		mustSort: Boolean,
		noDataText: {
			type: String,
			default: 'No data available'
		},
		options: Object,
		page: [Number, String],
		search: String,
		selectAllLabel: {
			type: String,
			default: 'Select all rows'
		},
		selectionColumnLabel: {
			type: String,
			default: 'Select rows'
		},
		serverItemsLength: {
			type: Number,
			default: -1
		},
		showSelect: Boolean,
		sortBy: [String, Array],
		sortDesc: [Boolean, Array]
	},
	computed: {
		other_listeners() {
			const { 'update:sort-by': _sortBy, 'update:sort-desc': _sortDesc, ...rest } = this.$listeners
			return rest
		},
		header_scoped_slots() {
			return Object.fromEntries(
				Object.entries(this.$scopedSlots).filter(([key]) => key.startsWith('header.'))
			)
		},
		other_scoped_slots() {
			const { item: _item, ...rest } = this.$scopedSlots
			return Object.fromEntries(
				Object.entries(rest).filter(([key]) => key !== 'data-table-select' && !key.startsWith('header.'))
			)
		},
	},
	mounted() {
		this.applyAccessibleHeaderBehavior()
	},
	updated() {
		this.applyAccessibleHeaderBehavior()
	},
	methods: {
		applyAccessibleHeaderBehavior() {
			const tableElement = this.$refs.vDataTable?.$el
			if (!tableElement) return
			const headerElements = tableElement.querySelectorAll('thead th')
			headerElements.forEach(header => {
				header.setAttribute('scope', 'col')
				const is_selection_header = Boolean(header.querySelector('.v-data-table__checkbox, [data-k-selection-header-control]'))
				if (is_selection_header) {
					header.setAttribute('aria-label', this.selectionColumnLabel)
					const selectionCheckbox = header.querySelector('.v-data-table__checkbox, .v-data-table__checkbox [role="checkbox"], .v-data-table__checkbox button, [data-k-selection-header-control]')
					if (selectionCheckbox) selectionCheckbox.setAttribute('aria-label', this.selectAllLabel)

					if (!header.querySelector('[data-k-selection-header-label]')) {
						const hiddenLabel = document.createElement('span')
						hiddenLabel.textContent = this.selectionColumnLabel
						hiddenLabel.setAttribute('data-k-selection-header-label', 'true')
						hiddenLabel.setAttribute('aria-hidden', 'true')
						hiddenLabel.style.position = 'absolute'
						hiddenLabel.style.width = '1px'
						hiddenLabel.style.height = '1px'
						hiddenLabel.style.padding = '0'
						hiddenLabel.style.margin = '-1px'
						hiddenLabel.style.overflow = 'hidden'
						hiddenLabel.style.clip = 'rect(0, 0, 0, 0)'
						hiddenLabel.style.whiteSpace = 'nowrap'
						hiddenLabel.style.border = '0'
						header.appendChild(hiddenLabel)
					}
					return
				}

				header.setAttribute('tabindex', '0')
				if (header.dataset.kAccessibleHeaderBound === 'true') return
				const spanElement = header.querySelector('span')
				header.style.outline = 'none'
				header.addEventListener('focus', () => {
					header.style.boxShadow = 'none'
					if (!spanElement) return
					spanElement.style.backgroundColor = 'rgba(13, 71, 161, 0.12)'
					spanElement.style.borderRadius = '4px'
					spanElement.style.textDecoration = 'underline'
					spanElement.style.textDecorationThickness = '2px'
					spanElement.style.textUnderlineOffset = '3px'
				})
				header.addEventListener('blur', () => {
					if (!spanElement) return
					spanElement.style.backgroundColor = ''
					spanElement.style.borderRadius = ''
					spanElement.style.textDecoration = ''
					spanElement.style.textDecorationThickness = ''
					spanElement.style.textUnderlineOffset = ''
				})
				header.addEventListener('keydown', (event) => {
					if (event.key == 'Enter' || event.key == ' ') {
						event.preventDefault()
						header.click()
					}
				})
				header.dataset.kAccessibleHeaderBound = 'true'
			})
		}
	}
};
</script>
